import { Observable, throwError as observableThrowError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AuthService } from './auth.service';
import { BaseModel, BaseModelConstructor } from './base.model';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { isBeaconstacDomainRequest } from '../shared/utils';
import { SsoAuthService } from './sso.service';

type BaseModelConstructorHackyAlias = BaseModelConstructor;

export interface ListResult<E> {
    objects: E[];
    pageCount: number;
    totalCount: number;
    next: any;
    previous: any;
    hiddenResponseCount?: number;
}

export interface SearchParams {
    [param: string]: string | number;
}

export abstract class BaseBackendService<E extends BaseModel> {
    protected apiURL: string;

    protected constructor(
        protected http: HttpClient,
        protected authService: AuthService,
        protected model: BaseModelConstructorHackyAlias,
        protected apiSuffix: string,
        private ssoService: SsoAuthService,
        protected apiVersion = environment.apiVersion
    ) {
        this.apiURL =
            environment.baseURL +
            environment.apiEndpoint +
            this.apiVersion +
            '/' +
            apiSuffix +
            '/';

        if (isBeaconstacDomainRequest()) {
            this.apiURL = this.apiURL.replace(
                environment.baseURL,
                environment.apiBaseURL
            );
        }
    }

    public getList(
        pageNumber: number = 1,
        pageSize: number = 10,
        params: SearchParams = {},
        passOrg: boolean = true,
        ordering?: string,
        custom?: any,
        extraParams?: string,
        passAllOrg = true,
        retry: number = 1
    ): Observable<any> {
        return this.http
            .get(
                this.buildURL(
                    this.apiURL,
                    pageNumber,
                    pageSize,
                    params,
                    passOrg,
                    ordering,
                    custom,
                    extraParams,
                    passAllOrg
                ),
                this.buildHeaders({})
            )
            .pipe(
                map((response): ListResult<E> => {
                    const jsonResponse = response;
                    const pageCount = Math.ceil(
                        jsonResponse['count'] / pageSize
                    );

                    return {
                        objects: jsonResponse['results'].map((result) => {
                            return new this.model(result);
                        }),
                        pageCount: pageCount,
                        totalCount: jsonResponse['count'],
                        next: jsonResponse['next'],
                        previous: jsonResponse['previous'],
                        hiddenResponseCount:
                            jsonResponse['hidden_response_count'],
                    };
                }),
                catchError(async (error) => {
                    // if 401 HTTP error, then call this function once again and stop recursion
                    if (error.status === 401 && retry > 0) {
                        await this.ssoService.refetchAccessToken();
                        retry = retry - 1;
                        return this.getList(
                            pageNumber,
                            pageSize,
                            params,
                            passOrg,
                            ordering,
                            custom,
                            extraParams,
                            passAllOrg,
                            retry
                        );
                    } else {
                        return this.handleError(error);
                    }
                })
            );
    }

    public getDetail(
        id: number | string,
        custom?: any,
        retry: number = 1
    ): Observable<any> {
        const beaconDetailUrl = ((this.apiURL + id) as string) + '/';
        const url = !custom
            ? this.buildURL(beaconDetailUrl)
            : this.buildURL(
                  beaconDetailUrl,
                  null,
                  null,
                  null,
                  true,
                  null,
                  custom
              );
        return this.http.get(url, this.buildHeaders({})).pipe(
            map((response) => {
                return new this.model(response);
            }),
            catchError(async (error) => {
                // if 401 HTTP error, then call this function once again and stop recursion
                if (error.status === 401 && retry > 0) {
                    await this.ssoService.refetchAccessToken();
                    retry = retry - 1;
                    return this.getDetail(id, custom || null, retry);
                } else {
                    return this.handleError(error);
                }
            })
        );
    }

    public getData(
        id?: number | string,
        custom?: any,
        retry: number = 1
    ): Observable<any> {
        let beaconDetailUrl = this.apiURL;
        if (id) {
            beaconDetailUrl += (id as string) + '/';
        }
        const url = !custom
            ? this.buildURL(beaconDetailUrl)
            : this.buildURL(
                  beaconDetailUrl,
                  null,
                  null,
                  null,
                  true,
                  null,
                  custom
              );
        return this.http.get(url, this.buildHeaders({})).pipe(
            map((response) => {
                return response;
            }),
            catchError(async (error) => {
                // if 401 HTTP error, then call this function once again and stop recursion
                if (error.status === 401 && retry > 0) {
                    await this.ssoService.refetchAccessToken();
                    retry = retry - 1;
                    return this.getData(id, custom || null, retry);
                } else {
                    return this.handleError(error);
                }
            })
        );
    }

    public put(
        body: object,
        id: number | string,
        custom?: string,
        retry: number = 1,
        addContentType: boolean = true
    ): Observable<any> {
        let url;
        url = ((this.apiURL + id) as string) + '/';
        if (custom) {
            url += custom;
        }
        return this.http
            .put(
                url,
                JSON.stringify(body),
                this.buildHeaders({}, addContentType)
            )
            .pipe(
                map((response) => {
                    return response;
                }),
                catchError(async (error) => {
                    // if 401 HTTP error, then call this function once again and stop recursion
                    if (error.status === 401 && retry > 0) {
                        await this.ssoService.refetchAccessToken();
                        retry = retry - 1;
                        return this.put(body, id, custom || null, retry);
                    } else {
                        return this.handleError(error);
                    }
                })
            );
    }

    public post(
        body: object,
        custom?: string,
        additionalConfig?: { observeResponse: boolean },
        addContentType: boolean = true,
        retry: number = 1
    ): Observable<any> {
        let url = this.apiURL;
        if (custom) {
            url += custom;
        } else {
            url = url + '?organization=' + this.authService.getCurrentOrgId();
        }

        const options =
            additionalConfig && additionalConfig.observeResponse
                ? {
                      ...this.buildHeaders({}, addContentType),
                      observe: 'response',
                  }
                : this.buildHeaders({}, addContentType);

        return this.http
            .post(
                url,
                body instanceof FormData ? body : JSON.stringify(body),
                options
            )
            .pipe(
                map((response) => {
                    return response;
                }),
                catchError(async (error) => {
                    // if 401 HTTP error, then call this function once again and stop recursion
                    if (error.status === 401 && retry > 0) {
                        await this.ssoService.refetchAccessToken();
                        retry = retry - 1;
                        return this.post(
                            body,
                            custom || null,
                            additionalConfig || null,
                            addContentType,
                            retry
                        );
                    } else {
                        return this.handleError(error);
                    }
                })
            );
    }

    public delete(
        id: number | string,
        custom?: string,
        retry: number = 1
    ): Observable<any> {
        let url;
        url = ((this.apiURL + id) as string) + '/';
        if (custom) {
            url += custom;
        }

        return this.http.delete(url, this.buildHeaders({})).pipe(
            map((response) => {
                return response;
            }),
            catchError(async (error) => {
                // if 401 HTTP error, then call this function once again and stop recursion
                if (error.status === 401 && retry > 0) {
                    await this.ssoService.refetchAccessToken();
                    retry = retry - 1;
                    return this.delete(id, custom || null, retry);
                } else {
                    return this.handleError(error);
                }
            })
        );
    }

    protected buildHeaders(
        argsObject: object,
        addContentType: boolean = true
    ): object {
        if (!argsObject) {
            // throw new Error('RequestOptionsArgs not passed to buildHeaders');
        }
        let headers = new HttpHeaders({
            Authorization: `${this.authService.getTokenType()} ${this.authService.getToken()}`,
        });
        if (addContentType && !(argsObject instanceof FormData)) {
            headers = headers.append('Content-Type', 'application/json');
        }
        argsObject['headers'] = headers;
        return argsObject;
    }

    protected buildURL(
        url: String = this.apiURL,
        pageNumber: number = 1,
        pageSize: number = 10,
        searchParams: SearchParams = {},
        passOrg: boolean = true,
        ordering?: string,
        custom?: string,
        extraParams: string = '',
        passAllOrg = true
    ): string {
        let paramString = '';
        // Build param string
        for (const param in searchParams) {
            const value = searchParams[param];
            paramString = paramString + '&' + param + '=' + value;
        }
        let orderingString = '';
        if (ordering) {
            orderingString = '&ordering=' + ordering;
        }
        if (custom) {
            return url + custom;
        } else if (passOrg) {
            return (
                url +
                '?organization=' +
                this.authService.getCurrentOrgId() +
                '&page_size=' +
                pageSize +
                '&page=' +
                pageNumber +
                paramString +
                orderingString +
                extraParams
            );
        } else {
            if (passAllOrg) {
                return (
                    url +
                    '?organization=ALL' +
                    '&page_size=' +
                    pageSize +
                    '&page=' +
                    pageNumber +
                    paramString +
                    orderingString +
                    extraParams
                );
            } else {
                return (
                    url +
                    '?page_size=' +
                    pageSize +
                    '&page=' +
                    pageNumber +
                    paramString +
                    orderingString +
                    extraParams
                );
            }
        }
    }

    protected handleError(error: any): Observable<any> {
        switch (error.status) {
            case 401:
                this.authService.logout();
                break;
            case 403:
                console.log('Forbidden 403');
                break;
        }
        // Manage error into something
        return observableThrowError(() => error);
    }
}
